import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, TextField, List, ListItemButton,
  ListItemText, CircularProgress, Alert, Box, IconButton,
  Avatar, ListItemAvatar, Chip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { apiFetch } from '../apiFetch';
import { useTutorial } from '../hooks/useTutorial';

interface GroceryWithPricing {
  id: number;
  name: string;
  brand?: string | null;
  category: string;
  packaging_size?: string | null;
  image?: string | null;
  image_thumbnail?: string | null;
  image_icon?: string | null;
  best_price?: number | null;
  best_price_shop_name?: string | null;
  price_count: number;
}

type FilterType = 'all' | 'unpriced' | 'priced';

const Prices: React.FC = () => {
  const [groceries, setGroceries] = useState<GroceryWithPricing[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  useTutorial('prices', { hasData: groceries.length > 0 });

  useEffect(() => {
    const fetchGroceriesWithPrices = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/groceries/with-prices/`);
        setGroceries(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching groceries.');
      } finally {
        setLoading(false);
      }
    };
    fetchGroceriesWithPrices();
  }, [apiUrl]);

  // Filter, sort groceries and calculate counts - memoized in single pass
  const { filteredGroceries, allCount, unpricedCount, pricedCount } = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();

    // First pass: filter by search term and calculate counts
    let allCnt = 0;
    let unpricedCnt = 0;
    let pricedCnt = 0;

    const searchFiltered = groceries.filter((grocery) => {
      const matchesSearch =
        grocery.name.toLowerCase().includes(lowerSearch) ||
        (grocery.brand && grocery.brand.toLowerCase().includes(lowerSearch)) ||
        grocery.category.toLowerCase().includes(lowerSearch);

      if (matchesSearch) {
        allCnt++;
        if (grocery.price_count === 0) unpricedCnt++;
        else pricedCnt++;
      }
      return matchesSearch;
    });

    // Second pass: apply pricing filter
    const filtered = searchFiltered.filter((grocery) => {
      if (filter === 'unpriced') return grocery.price_count === 0;
      if (filter === 'priced') return grocery.price_count > 0;
      return true;
    });

    // Sort: unpriced first, then alphabetical
    filtered.sort((a, b) => {
      if (a.price_count === 0 && b.price_count > 0) return -1;
      if (a.price_count > 0 && b.price_count === 0) return 1;
      return a.name.localeCompare(b.name);
    });

    return { filteredGroceries: filtered, allCount: allCnt, unpricedCount: unpricedCnt, pricedCount: pricedCnt };
  }, [groceries, searchTerm, filter]);

  // Handle filter change
  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: FilterType | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  // Handle grocery click - navigate to grocery price page
  const handleGroceryClick = (groceryId: number) => {
    navigate(`/prices/grocery/${groceryId}`);
  };

  // Format secondary text for list item
  const formatSecondaryText = (grocery: GroceryWithPricing): string => {
    const parts = [];
    if (grocery.brand) parts.push(grocery.brand);
    if (grocery.category) parts.push(grocery.category);
    if (grocery.packaging_size) parts.push(grocery.packaging_size);
    return parts.join(' | ');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Prices</Typography>
        </Box>
      </Box>
      <Typography variant="body1" gutterBottom data-tutorial="prices-description">
        Track and compare grocery prices across your stores. Select a grocery to view and manage its prices at different stores.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Search bar */}
      <TextField
        label="Search Groceries"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        data-tutorial="prices-search"
      />

      {/* Filter toggle */}
      <Box sx={{ mb: 2 }} data-tutorial="prices-filter">
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          size="small"
          fullWidth
        >
          <ToggleButton value="all">
            All ({allCount})
          </ToggleButton>
          <ToggleButton value="unpriced">
            Unpriced ({unpricedCount})
          </ToggleButton>
          <ToggleButton value="priced">
            Priced ({pricedCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}

      <List data-tutorial="prices-list">
        {filteredGroceries.map((grocery) => (
          <ListItemButton
            key={grocery.id}
            onClick={() => handleGroceryClick(grocery.id)}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <ListItemAvatar>
              <Avatar
                src={grocery.image_icon || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
                variant="rounded"
                sx={{ width: 40, height: 40 }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={grocery.name}
              secondary={formatSecondaryText(grocery)}
              sx={{ flex: 1 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              {grocery.price_count === 0 ? (
                <Chip
                  label="Needs pricing"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              ) : (
                <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    ${grocery.best_price?.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {grocery.price_count} {grocery.price_count === 1 ? 'store' : 'stores'}
                  </Typography>
                </Box>
              )}
              <ArrowForwardIosIcon fontSize="small" color="action" />
            </Box>
          </ListItemButton>
        ))}
      </List>

      {!loading && groceries.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No groceries yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add groceries first before tracking prices.
          </Typography>
        </Box>
      )}

      {!loading && groceries.length > 0 && filteredGroceries.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No matching groceries
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default Prices;
